/**
 * Admin ops overlay for destination-campaign hotels.
 * Built-in Belvedere / Royal Tulip stay in JS registries; new hotels and extra
 * rooms persist here (localStorage) so ops can onboard without a deploy.
 */
import { belvedereContract } from "./belvedereContractData";
import { royalTulipTbilisiContract } from "./royalTulipTbilisiContractData";
import { royalZanzibarBeachResortContract } from "./royalZanzibarBeachResortContractData";
import {
  campaignFacilitiesToRoomNames,
  normalizeCampaignFacilityRecords,
} from "./campaignFacilityCatalog";
import { normalizeVoucherRecipients } from "./campaignVoucherRecipients";

export const CAMPAIGN_HOTEL_OPS_STORAGE_KEY = "hr.campaignHotelOpsOverlay.v1";
export const CAMPAIGN_HOTEL_OPS_QUOTE_KIND = "ops";
export const CAMPAIGN_HOTEL_OVERLAY_CHANGED_EVENT = "hr-campaign-hotel-overlay-changed";

export const DEFAULT_AGENT_MARKUP_PERCENT = 10;
export const DEFAULT_PUBLIC_MARKUP_PERCENT = 20;
export const DEFAULT_PUBLIC_LIST_MARKUP_PERCENT = 30;
export const DEFAULT_PUBLIC_SALE_DISCOUNT_PERCENT = 10;
export const DEFAULT_CHILD_RATE_PERCENT = 50;
export const DEFAULT_CHILD_AGE_FROM = 2;
export const DEFAULT_CHILD_AGE_TO = 12;

export const ZANZIBAR_CANONICAL_CAMPAIGN_CODE = "royal-zanzibar-beach-resort-2026";
export const ZANZIBAR_CAMPAIGN_CODE_ALIASES = Object.freeze([
  "royal-zanzibar-2026",
  "royal-zanzibar",
]);

export const BUILT_IN_CAMPAIGN_CODES = Object.freeze([
  String(royalTulipTbilisiContract.hotelCode),
  String(belvedereContract.hotelCode),
  // Zanzibar is a JS contract hotel; keep it built-in so commercial edits write
  // hotelPatches (pricing) instead of a disconnected overlay hotel row.
  String(royalZanzibarBeachResortContract.hotelCode),
]);

export const ROOM_SOURCE = Object.freeze({
  persisted: "persisted",
  builtIn: "built-in",
  inferredInventory: "inferred-inventory",
});

export function normalizeCampaignHotelCode(value) {
  return String(value || "").trim().toLowerCase().replace(/_/g, "-");
}

export function campaignHotelAliasGroup(code) {
  const norm = normalizeCampaignHotelCode(code);
  if (!norm) return "";
  if (norm === ZANZIBAR_CANONICAL_CAMPAIGN_CODE || ZANZIBAR_CAMPAIGN_CODE_ALIASES.includes(norm)) {
    return ZANZIBAR_CANONICAL_CAMPAIGN_CODE;
  }
  return norm;
}

export function campaignHotelCodesAreAliases(a, b) {
  const left = campaignHotelAliasGroup(a);
  const right = campaignHotelAliasGroup(b);
  return Boolean(left && right && left === right);
}

export function isInferredInventoryRoomName(name, code) {
  const label = String(name || "").trim();
  const roomCode = Number(code);
  if (!label || !Number.isFinite(roomCode) || roomCode <= 0) return false;
  return new RegExp(`^Room\\s*${roomCode}$`, "i").test(label);
}

export function isInferredInventoryRoomArtifact(room, { inferredHotel = false } = {}) {
  if (!room || typeof room !== "object") return false;
  if (room.opsOverlay) return false;
  if (room.roomSource === ROOM_SOURCE.persisted || room.roomSource === ROOM_SOURCE.builtIn) {
    return false;
  }
  if (inferredHotel || room.inferred === true || room.roomSource === ROOM_SOURCE.inferredInventory) {
    return true;
  }
  const name = room.name || room.nameHe || room.name_he || room.display_name;
  const code = room.code ?? room.room_code;
  return isInferredInventoryRoomName(name, code);
}

export function roomsEligibleForCatalogPersist(rooms, { inferredHotel = false } = {}) {
  return (Array.isArray(rooms) ? rooms : []).filter((room) => {
    if (!room || !String(room.name || "").trim()) return false;
    if (room.opsOverlay || room.roomSource === ROOM_SOURCE.persisted) return true;
    return !isInferredInventoryRoomArtifact(room, { inferredHotel });
  });
}

function pickAliasWinnerCode(codes, backendCanonicalCodes = []) {
  const list = (codes || []).map(normalizeCampaignHotelCode).filter(Boolean);
  if (!list.length) return "";
  const group = campaignHotelAliasGroup(list[0]);
  const backendHit = (backendCanonicalCodes || [])
    .map(normalizeCampaignHotelCode)
    .find((code) => campaignHotelAliasGroup(code) === group);
  if (backendHit) return backendHit;
  if (list.includes(group)) return group;
  return list[0];
}

function mergeOverlayHotelPair(winner, other) {
  const winnerRooms = roomsEligibleForCatalogPersist(winner?.rooms);
  const otherRooms = roomsEligibleForCatalogPersist(other?.rooms);
  const byCode = new Map();
  for (const room of [...otherRooms, ...winnerRooms]) {
    const key = String(room.code ?? room.room_code ?? "");
    if (key) byCode.set(key, room);
  }
  return {
    ...other,
    ...winner,
    hotelCode: winner.hotelCode,
    slug: winner.slug || winner.hotelCode,
    rooms: [...byCode.values()],
    inferred: Boolean(winner.inferred && other.inferred),
    createdAt: winner.createdAt || other.createdAt,
    updatedAt: winner.updatedAt || other.updatedAt,
  };
}

export function collapseOverlayHotelsByAlias(hotels, { backendCanonicalCodes = [] } = {}) {
  const groups = new Map();
  for (const hotel of hotels || []) {
    if (!hotel?.hotelCode) continue;
    const group = campaignHotelAliasGroup(hotel.hotelCode);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(hotel);
  }
  const collapsed = [];
  for (const groupHotels of groups.values()) {
    const winnerCode = pickAliasWinnerCode(
      groupHotels.map((row) => row.hotelCode),
      backendCanonicalCodes,
    );
    const preferred =
      groupHotels.find((row) => normalizeCampaignHotelCode(row.hotelCode) === winnerCode) ||
      groupHotels[0];
    const merged = groupHotels.reduce((acc, row) => {
      if (row === preferred) return acc;
      return mergeOverlayHotelPair(acc, row);
    }, { ...preferred, hotelCode: winnerCode, slug: preferred.slug || winnerCode });
    collapsed.push(merged);
  }
  return collapsed;
}

function rewriteOverlayKeyedMap(map, keyRewrites) {
  if (!map || typeof map !== "object") return {};
  const next = {};
  for (const [key, value] of Object.entries(map)) {
    const dest = keyRewrites[key] || key;
    if (next[dest] == null) next[dest] = value;
  }
  return next;
}

export function collapseOverlayStateAliases(state, { backendCanonicalCodes = [] } = {}) {
  const hotels = collapseOverlayHotelsByAlias(state?.hotels || [], { backendCanonicalCodes });
  const keyRewrites = {};
  for (const hotel of state?.hotels || []) {
    const group = campaignHotelAliasGroup(hotel.hotelCode);
    const winner = hotels.find((row) => campaignHotelAliasGroup(row.hotelCode) === group);
    if (winner && hotel.hotelCode !== winner.hotelCode) {
      keyRewrites[hotel.hotelCode] = winner.hotelCode;
    }
  }
  return {
    ...state,
    hotels,
    extraRoomsByHotel: rewriteOverlayKeyedMap(state?.extraRoomsByHotel, keyRewrites),
    hiddenRoomCodesByHotel: rewriteOverlayKeyedMap(state?.hiddenRoomCodesByHotel, keyRewrites),
    voucherRecipientsByHotel: rewriteOverlayKeyedMap(state?.voucherRecipientsByHotel, keyRewrites),
    hotelPatchesByHotel: rewriteOverlayKeyedMap(state?.hotelPatchesByHotel, keyRewrites),
    hiddenHotelCodes: [...new Set((state?.hiddenHotelCodes || []).map((code) => keyRewrites[code] || code))],
  };
}

export const CAMPAIGN_BOARD_OPTIONS = [
  { value: "BB", label: "BB · לינה וארוחת בוקר", labelHe: "לינה וארוחת בוקר" },
  { value: "HB", label: "HB · חצי פנסיון", labelHe: "חצי פנסיון" },
  { value: "FB", label: "FB · פנסיון מלא", labelHe: "פנסיון מלא" },
  { value: "AI", label: "AI · הכל כלול", labelHe: "הכל כלול" },
  { value: "RO", label: "RO · לינה בלבד", labelHe: "לינה בלבד" },
];

const KOSHER_BOARD_HE = {
  BB: "ארוחת בוקר כשרה",
  HB: "חצי פנסיון כשר",
  FB: "פנסיון מלא כשר",
  AI: "הכל כלול כשר",
  RO: "לינה בלבד",
};

export function emptyOverlayState() {
  return {
    version: 1,
    hotels: [],
    extraRoomsByHotel: {},
    hiddenHotelCodes: [],
    hiddenRoomCodesByHotel: {},
    voucherRecipientsByHotel: {},
    hotelPatchesByHotel: {},
  };
}

let memoryOverlayState = emptyOverlayState();

function defaultStorage() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* ignore */
  }
  return null;
}

function cloneOverlayState(state) {
  return {
    version: 1,
    hotels: [...(state?.hotels || [])],
    extraRoomsByHotel: { ...(state?.extraRoomsByHotel || {}) },
    hiddenHotelCodes: [...(state?.hiddenHotelCodes || [])],
    hiddenRoomCodesByHotel: { ...(state?.hiddenRoomCodesByHotel || {}) },
    voucherRecipientsByHotel: { ...(state?.voucherRecipientsByHotel || {}) },
    hotelPatchesByHotel: { ...(state?.hotelPatchesByHotel || {}) },
  };
}

function finiteMarkupPercent(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function resetCampaignHotelOverlay(storage = defaultStorage()) {
  memoryOverlayState = emptyOverlayState();
  try {
    storage?.removeItem?.(CAMPAIGN_HOTEL_OPS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function emitOverlayChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CAMPAIGN_HOTEL_OVERLAY_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

export function isBuiltInCampaignCode(hotelCode) {
  const code = String(hotelCode || "").trim();
  if (!code) return false;
  if (BUILT_IN_CAMPAIGN_CODES.includes(code)) return true;
  // Alias forms of built-in Zanzibar must behave like catalog hotels (patch, not overlay row).
  return campaignHotelCodesAreAliases(code, ZANZIBAR_CANONICAL_CAMPAIGN_CODE);
}

export function builtInContractForCode(hotelCode) {
  const code = String(hotelCode || "").trim();
  if (code === String(royalTulipTbilisiContract.hotelCode)) return royalTulipTbilisiContract;
  if (code === String(belvedereContract.hotelCode)) return belvedereContract;
  if (
    code === String(royalZanzibarBeachResortContract.hotelCode) ||
    campaignHotelCodesAreAliases(code, royalZanzibarBeachResortContract.hotelCode)
  ) {
    return royalZanzibarBeachResortContract;
  }
  return null;
}

export function slugifyCampaignCode(name, year = 2026) {
  const base = String(name || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (!base) return "";
  return base.endsWith(String(year)) ? base : `${base}-${year}`;
}

export function campaignCodeIsValid(code) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(code || "").trim());
}

export function boardLabelHe(board, kosher = false) {
  const code = String(board || "").toUpperCase();
  if (kosher && KOSHER_BOARD_HE[code]) return KOSHER_BOARD_HE[code];
  const hit = CAMPAIGN_BOARD_OPTIONS.find((row) => row.value === code);
  return hit?.labelHe || code || "";
}

export function buildDuplicateNamePattern(displayName) {
  const words = String(displayName || "")
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .slice(0, 3)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return null;
  try {
    return new RegExp(words.join("\\s*"), "i");
  } catch {
    return null;
  }
}

export function occupancyLabelFromDraft(draft) {
  const maxAdults = Math.max(1, Number(draft?.maxAdults) || 2);
  const maxChildren = Math.max(0, Number(draft?.maxChildren) || 0);
  if (maxAdults === 1 && maxChildren === 0) return "מבוגר יחיד";
  if (maxChildren > 0) return `עד ${maxAdults} מבוגרים + עד ${maxChildren} ילדים`;
  return `${maxAdults} מבוגרים`;
}

export function nextNumericRoomCode(rooms) {
  const codes = (Array.isArray(rooms) ? rooms : [])
    .map((row) => Number(row?.code ?? row?.room_code))
    .filter((code) => Number.isFinite(code) && code > 0);
  return codes.length ? Math.max(...codes) + 1 : 1;
}

export function normalizeSeasonRows(seasons, fallbackFrom, fallbackTo, minStayNights = 1) {
  const list = Array.isArray(seasons) ? seasons.filter((row) => row?.from && row?.to) : [];
  if (list.length) {
    return list.map((row, index) => ({
      code: String(row.code || String.fromCharCode(65 + index)).toUpperCase(),
      from: String(row.from).slice(0, 10),
      to: String(row.to).slice(0, 10),
      minStayNights: Math.max(1, Number(row.minStayNights) || minStayNights),
      releaseNights: Math.max(1, Number(row.releaseNights) || 1),
    }));
  }
  if (fallbackFrom && fallbackTo) {
    return [
      {
        code: "A",
        from: String(fallbackFrom).slice(0, 10),
        to: String(fallbackTo).slice(0, 10),
        minStayNights: Math.max(1, Number(minStayNights) || 1),
        releaseNights: 1,
      },
    ];
  }
  return [];
}

function isBlackoutDate(iso, blackouts) {
  const day = String(iso || "").slice(0, 10);
  if (!day) return false;
  return (Array.isArray(blackouts) ? blackouts : []).some((row) => {
    const from = String(row?.from || "").slice(0, 10);
    const to = String(row?.to || "").slice(0, 10);
    return from && to && day >= from && day <= to;
  });
}

export function buildOpsResolveSeasonForDate(seasons, blackouts) {
  const list = Array.isArray(seasons) ? seasons : [];
  return (iso) => {
    if (isBlackoutDate(iso, blackouts)) return null;
    const day = String(iso || "").slice(0, 10);
    const hit = list.find((season) => day >= season.from && day <= season.to);
    return hit?.code || null;
  };
}

export function normalizeOpsRoomPricing(draft = {}) {
  const pricingKind =
    draft.pricingKind === "supplement" || draft.pricingKind === "base" ? draft.pricingKind : null;
  const adultNightlyNet = Number(
    draft.adultNightlyNet != null
      ? draft.adultNightlyNet
      : pricingKind === "base"
        ? draft.nightlyNet
        : Number.NaN,
  );
  const childRatePercent = Number(draft.childRatePercent);
  const childAgeFrom = Number(draft.childAgeFrom);
  const childAgeTo = Number(draft.childAgeTo);
  const supplementAdult = Number(draft.supplementAdultPerNight);
  const supplementChild = Number(draft.supplementChildPerNight);
  const supplementChildPct = Number(draft.supplementChildPercentOfAdult);
  const stayFrom = draft.stayFrom || draft.seasonFrom || null;
  const stayTo = draft.stayTo || draft.seasonTo || null;
  return {
    pricingKind,
    adultNightlyNet:
      Number.isFinite(adultNightlyNet) && adultNightlyNet >= 0 ? adultNightlyNet : null,
    childRatePercent:
      Number.isFinite(childRatePercent) && childRatePercent >= 0
        ? childRatePercent
        : DEFAULT_CHILD_RATE_PERCENT,
    childAgeFrom: Number.isFinite(childAgeFrom) ? childAgeFrom : DEFAULT_CHILD_AGE_FROM,
    childAgeTo: Number.isFinite(childAgeTo) ? childAgeTo : DEFAULT_CHILD_AGE_TO,
    baseRoomCode:
      draft.baseRoomCode != null && String(draft.baseRoomCode).trim()
        ? String(draft.baseRoomCode).trim()
        : null,
    baseRoomId:
      draft.baseRoomId != null && String(draft.baseRoomId).trim()
        ? String(draft.baseRoomId).trim()
        : null,
    supplementAdultPerNight: Number.isFinite(supplementAdult) ? supplementAdult : null,
    supplementChildPerNight: Number.isFinite(supplementChild) ? supplementChild : null,
    supplementChildPercentOfAdult:
      Number.isFinite(supplementChildPct) && supplementChildPct >= 0
        ? supplementChildPct
        : DEFAULT_CHILD_RATE_PERCENT,
    onRequest: Boolean(draft.onRequest),
    fullPrepayment: Boolean(draft.fullPrepayment),
    stayFrom: stayFrom ? String(stayFrom).slice(0, 10) : null,
    stayTo: stayTo ? String(stayTo).slice(0, 10) : null,
  };
}

export function expandRoomDraftToContractRooms(draft, startCode, { kosher = false, seasons = [] } = {}) {
  const boards = (Array.isArray(draft?.boards) && draft.boards.length
    ? draft.boards
    : [draft?.board || "BB"]
  ).map((board) => String(board || "BB").toUpperCase());
  const uniqueBoards = [...new Set(boards)];
  const name = String(draft?.name || "").trim();
  const nameHe = String(draft?.nameHe || "").trim();
  const maxAdults = Math.max(1, Number(draft?.maxAdults) || 2);
  const minAdults = Math.max(1, Math.min(maxAdults, Number(draft?.minAdults) || 1));
  const maxChildren = Math.max(0, Number(draft?.maxChildren) || 0);
  const maxOccupancy = Math.max(
    maxAdults,
    Number(draft?.maxOccupancy) || maxAdults + maxChildren,
  );
  const minOccupancy = Math.max(1, Number(draft?.minOccupancy) || minAdults);
  const allotment = Math.max(0, Number(draft?.allotment) || 1);
  const pricing = normalizeOpsRoomPricing(draft);
  const chargeType =
    pricing.pricingKind === "base" || pricing.pricingKind === "supplement"
      ? "per_adult"
      : draft?.chargeType === "per_person"
        ? "per_person"
        : "per_unit";
  const nightlyNet = Number(draft?.nightlyNet ?? pricing.adultNightlyNet);
  const seasonalPrices = {};
  for (const season of seasons) {
    const override = Number(draft?.seasonalRates?.[season.code]);
    seasonalPrices[season.code] = Number.isFinite(override)
      ? override
      : Number.isFinite(nightlyNet)
        ? nightlyNet
        : 0;
  }
  const baseId = String(draft?.id || slugifyCampaignCode(name, "").replace(/-?\d*$/, "") || "room")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  let code = Math.max(1, Number(draft?.code) || Number(startCode) || 1);
  return uniqueBoards.map((board) => {
    const room = {
      code,
      id: uniqueBoards.length > 1 ? `${baseId}-${board.toLowerCase()}` : baseId || `room-${code}`,
      name: uniqueBoards.length > 1 ? `${name} ${board}` : name,
      nameHe: nameHe
        ? uniqueBoards.length > 1
          ? `${nameHe} · ${boardLabelHe(board, kosher)}`
          : nameHe
        : "",
      board,
      boardHe: boardLabelHe(board, kosher),
      chargeType,
      allotment,
      minAdults,
      maxAdults,
      maxChildren,
      minOccupancy,
      maxOccupancy,
      infantsAllowed: draft?.infantsAllowed !== false,
      occupancyLabel: occupancyLabelFromDraft({ maxAdults, maxChildren }),
      seasonalPrices: { ...seasonalPrices },
      ...(pricing.pricingKind ? pricing : {}),
      roomFacilities: normalizeCampaignFacilityRecords(
        draft?.roomFacilities || draft?.facilities,
        "room",
      ),
      facilities: campaignFacilitiesToRoomNames(
        draft?.roomFacilities || draft?.facilities,
      ),
      opsOverlay: true,
    };
    code += 1;
    return room;
  });
}

export function normalizeOverlayHotel(raw) {
  if (!raw || typeof raw !== "object") return null;
  const hotelCode = String(raw.hotelCode || raw.slug || "").trim();
  const displayName = String(raw.displayName || raw.hotelName || "").trim();
  if (!hotelCode || !displayName) return null;
  const stayFrom = raw.stayFrom || raw.seasons?.[0]?.from || null;
  const stayTo = raw.stayTo || raw.seasons?.[raw.seasons?.length - 1]?.to || null;
  const minStayNights = Math.max(1, Number(raw.minStayNights) || 1);
  const seasons = normalizeSeasonRows(raw.seasons, stayFrom, stayTo, minStayNights);
  const city = String(raw.city || "").trim();
  const country = String(raw.country || "").trim();
  const location = String(raw.location || [city, country].filter(Boolean).join(", ")).trim();
  const locationHe = String(raw.locationHe || raw.displayNameHe || location).trim();
  const destinationCodes = (Array.isArray(raw.destinationCodes) ? raw.destinationCodes : [])
    .map((code) => String(code || "").trim().toUpperCase())
    .filter((code) => /^[A-Z]{3}$/.test(code));
  const destinationNameHints = [
    ...(Array.isArray(raw.destinationNameHints) ? raw.destinationNameHints : []),
    city,
    country,
    raw.displayNameHe,
    locationHe,
  ]
    .map((hint) => String(hint || "").trim())
    .filter(Boolean);
  return {
    hotelCode,
    slug: String(raw.slug || hotelCode).trim(),
    displayName,
    displayNameHe: String(raw.displayNameHe || displayName).trim(),
    city,
    country,
    location,
    locationHe,
    destinationCodes,
    destinationNameHints: [...new Set(destinationNameHints)],
    gimmonixHotelId:
      raw.gimmonixHotelId != null && String(raw.gimmonixHotelId).trim()
        ? String(raw.gimmonixHotelId).trim()
        : null,
    currency: String(raw.currency || "EUR").toUpperCase() === "USD" ? "USD" : "EUR",
    kosher: Boolean(raw.kosher),
    starRating: Math.max(1, Math.min(5, Number(raw.starRating) || 4)),
    agentMarkupPercent: finiteMarkupPercent(raw.agentMarkupPercent, DEFAULT_AGENT_MARKUP_PERCENT),
    publicEffectiveMarkupTargetPercent: finiteMarkupPercent(
      raw.publicEffectiveMarkupTargetPercent ?? raw.publicMarkupPercent,
      DEFAULT_PUBLIC_MARKUP_PERCENT,
    ),
    publicListMarkupPercent: DEFAULT_PUBLIC_LIST_MARKUP_PERCENT,
    publicSaleDiscountPercent: DEFAULT_PUBLIC_SALE_DISCOUNT_PERCENT,
    minStayNights,
    defaultAllotment: Math.max(0, Number(raw.defaultAllotment) || 1),
    seasons,
    blackouts: (Array.isArray(raw.blackouts) ? raw.blackouts : [])
      .filter((row) => row?.from && row?.to)
      .map((row) => ({
        from: String(row.from).slice(0, 10),
        to: String(row.to).slice(0, 10),
      })),
    rooms: roomsEligibleForCatalogPersist(raw.rooms, { inferredHotel: Boolean(raw.inferred) }),
    hotelFacilities: normalizeCampaignFacilityRecords(raw.hotelFacilities, "hotel"),
    voucherRecipients: normalizeVoucherRecipients(raw.voucherRecipients),
    quoteKind: CAMPAIGN_HOTEL_OPS_QUOTE_KIND,
    skipInventoryHold: false,
    inferred: Boolean(raw.inferred),
    roomSource: raw.inferred ? ROOM_SOURCE.inferredInventory : ROOM_SOURCE.persisted,
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
}

export function normalizeHotelPatch(raw) {
  if (!raw || typeof raw !== "object") return null;
  const patch = {};
  if (raw.displayName != null) patch.displayName = String(raw.displayName).trim();
  if (raw.displayNameHe != null) patch.displayNameHe = String(raw.displayNameHe).trim();
  if (raw.city != null) patch.city = String(raw.city).trim();
  if (raw.country != null) patch.country = String(raw.country).trim();
  if (raw.location != null) patch.location = String(raw.location).trim();
  if (raw.locationHe != null) patch.locationHe = String(raw.locationHe).trim();
  if (Array.isArray(raw.destinationCodes)) {
    patch.destinationCodes = raw.destinationCodes
      .map((code) => String(code || "").trim().toUpperCase())
      .filter((code) => /^[A-Z]{3}$/.test(code));
  }
  if (Array.isArray(raw.destinationNameHints)) {
    patch.destinationNameHints = raw.destinationNameHints
      .map((hint) => String(hint || "").trim())
      .filter(Boolean);
  }
  if (raw.gimmonixHotelId !== undefined) {
    patch.gimmonixHotelId =
      raw.gimmonixHotelId != null && String(raw.gimmonixHotelId).trim()
        ? String(raw.gimmonixHotelId).trim()
        : null;
  }
  if (raw.currency != null) {
    patch.currency = String(raw.currency || "EUR").toUpperCase() === "USD" ? "USD" : "EUR";
  }
  if (raw.kosher === true || raw.kosher === false) patch.kosher = Boolean(raw.kosher);
  if (raw.starRating != null && Number.isFinite(Number(raw.starRating))) {
    patch.starRating = Math.max(1, Math.min(5, Number(raw.starRating)));
  }
  if (raw.agentMarkupPercent != null && raw.agentMarkupPercent !== "") {
    const n = Number(raw.agentMarkupPercent);
    if (Number.isFinite(n)) patch.agentMarkupPercent = Math.max(0, n);
  }
  const publicPct = raw.publicEffectiveMarkupTargetPercent ?? raw.publicMarkupPercent;
  if (publicPct != null && publicPct !== "") {
    const n = Number(publicPct);
    if (Number.isFinite(n)) patch.publicEffectiveMarkupTargetPercent = Math.max(0, n);
  }
  if (raw.minStayNights != null && Number.isFinite(Number(raw.minStayNights))) {
    patch.minStayNights = Math.max(1, Number(raw.minStayNights));
  }
  if (raw.defaultAllotment != null && Number.isFinite(Number(raw.defaultAllotment))) {
    patch.defaultAllotment = Math.max(0, Number(raw.defaultAllotment));
  }
  if (Array.isArray(raw.seasons)) patch.seasons = normalizeSeasonRows(raw.seasons);
  if (Array.isArray(raw.blackouts)) {
    patch.blackouts = raw.blackouts
      .filter((row) => row?.from && row?.to)
      .map((row) => ({
        from: String(row.from).slice(0, 10),
        to: String(row.to).slice(0, 10),
      }));
  }
  if (raw.hotelFacilities != null) {
    patch.hotelFacilities = normalizeCampaignFacilityRecords(raw.hotelFacilities, "hotel");
  }
  if (raw.voucherRecipients != null) {
    patch.voucherRecipients = normalizeVoucherRecipients(raw.voucherRecipients);
  }
  return Object.keys(patch).length ? patch : null;
}

export function getOverlayHotelPatch(hotelCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  if (!code) return null;
  const map = readCampaignHotelOverlay(storage).hotelPatchesByHotel || {};
  if (map[code]) return map[code];
  const group = campaignHotelAliasGroup(code);
  if (!group) return null;
  for (const [key, patch] of Object.entries(map)) {
    if (campaignHotelAliasGroup(key) === group) return patch;
  }
  return null;
}

export function applyHotelPatchToContract(contract, hotelCode, storage = defaultStorage()) {
  if (!contract) return null;
  const patch = getOverlayHotelPatch(hotelCode, storage);
  if (!patch) return contract;
  const next = { ...contract };
  if (patch.displayName) next.hotelName = patch.displayName;
  if (patch.displayNameHe) next.hotelNameHe = patch.displayNameHe;
  if (patch.location) next.location = patch.location;
  if (patch.locationHe) next.locationHe = patch.locationHe;
  if (patch.starRating != null) next.starRating = patch.starRating;
  if (patch.kosher != null) next.kosher = patch.kosher;
  if (patch.currency) next.currency = patch.currency;
  if (patch.agentMarkupPercent != null) next.agentMarkupPercent = patch.agentMarkupPercent;
  if (patch.publicEffectiveMarkupTargetPercent != null) {
    next.publicEffectiveMarkupTargetPercent = patch.publicEffectiveMarkupTargetPercent;
    next.defaultMarkupPercent = patch.publicEffectiveMarkupTargetPercent;
  }
  if (patch.minStayNights != null) next.minStayNights = patch.minStayNights;
  return next;
}

export function applyHotelPatchToCampaignRow(row, storage = defaultStorage()) {
  if (!row) return row;
  const patch = getOverlayHotelPatch(row.hotelCode, storage);
  if (!patch) return row;
  const kosher = patch.kosher != null ? Boolean(patch.kosher) : row.kosher;
  return {
    ...row,
    displayName: patch.displayName || row.displayName,
    address: patch.locationHe || patch.location || row.address,
    starRating: patch.starRating ?? row.starRating,
    currency: patch.currency || row.currency,
    kosher,
    gimmonixHotelId:
      patch.gimmonixHotelId !== undefined ? patch.gimmonixHotelId : row.gimmonixHotelId,
    destinationCodes: patch.destinationCodes?.length ? patch.destinationCodes : row.destinationCodes,
    destinationNameHints: patch.destinationNameHints?.length
      ? patch.destinationNameHints
      : row.destinationNameHints,
    badgeText: kosher ? "מחירים מיוחדים · כשר" : "מחירים מיוחדים",
    duplicateNamePattern: patch.displayName
      ? buildDuplicateNamePattern(patch.displayName) || row.duplicateNamePattern
      : row.duplicateNamePattern,
  };
}

export function campaignHotelLogSnapshot(hotelCode, storage = defaultStorage()) {
  const overlay = getOverlayHotel(hotelCode, storage);
  const patch = getOverlayHotelPatch(hotelCode, storage);
  const builtIn = builtInContractForCode(hotelCode);
  return {
    displayName: overlay?.displayName || patch?.displayName || builtIn?.hotelName || null,
    displayNameHe: overlay?.displayNameHe || patch?.displayNameHe || builtIn?.hotelNameHe || null,
    agentMarkupPercent:
      overlay?.agentMarkupPercent ??
      patch?.agentMarkupPercent ??
      builtIn?.agentMarkupPercent ??
      null,
    publicMarkupPercent:
      overlay?.publicEffectiveMarkupTargetPercent ??
      patch?.publicEffectiveMarkupTargetPercent ??
      builtIn?.publicEffectiveMarkupTargetPercent ??
      null,
    currency: overlay?.currency || patch?.currency || builtIn?.currency || null,
    kosher: overlay?.kosher ?? patch?.kosher ?? builtIn?.kosher ?? null,
    city: overlay?.city || patch?.city || null,
    destinationCodes: overlay?.destinationCodes || patch?.destinationCodes || null,
    gimmonixHotelId: overlay?.gimmonixHotelId ?? patch?.gimmonixHotelId ?? null,
  };
}

export function buildOpsContractFromHotel(hotel) {
  const row = normalizeOverlayHotel(hotel);
  if (!row) return null;
  return {
    hotelCode: row.hotelCode,
    hotelName: row.displayName,
    hotelNameHe: row.displayNameHe,
    location: row.location,
    locationHe: row.locationHe,
    starRating: row.starRating,
    kosher: row.kosher,
    board: row.rooms[0]?.board || "BB",
    boardHe: row.rooms[0]?.boardHe || boardLabelHe("BB", row.kosher),
    seasonLabel: row.seasons.length
      ? `${row.seasons[0].from} – ${row.seasons[row.seasons.length - 1].to}`
      : "",
    currency: row.currency,
    publicListMarkupPercent: row.publicListMarkupPercent,
    publicEffectiveMarkupTargetPercent: row.publicEffectiveMarkupTargetPercent,
    publicSaleDiscountPercent: row.publicSaleDiscountPercent,
    agentMarkupPercent: row.agentMarkupPercent,
    defaultMarkupPercent: row.publicEffectiveMarkupTargetPercent,
    minStayNights: row.minStayNights,
    releaseNights: 1,
    accommodationTaxPerRoomPerNight: 0,
    outOfSeasonMessage: "חלק מתאריכי השהייה מחוץ לחלון החוזה או בימי חסימה.",
    resolveSeasonForDate: buildOpsResolveSeasonForDate(row.seasons, row.blackouts),
    seasons: row.seasons,
    roomTypes: row.rooms,
    blackouts: row.blackouts,
    opsOverlay: true,
  };
}

export function buildOpsCampaignRow(hotel) {
  const row = normalizeOverlayHotel(hotel);
  if (!row) return null;
  const kosher = Boolean(row.kosher);
  return {
    id: `campaign-${row.slug}`,
    destinationCodes: row.destinationCodes,
    destinationNameHints: row.destinationNameHints,
    duplicateNamePattern: buildDuplicateNamePattern(row.displayName),
    slug: row.slug,
    publicPath: null,
    displayName: row.displayName,
    address: row.locationHe || row.location,
    starRating: row.starRating,
    image: null,
    currency: row.currency,
    quoteKind: CAMPAIGN_HOTEL_OPS_QUOTE_KIND,
    hotelCode: row.hotelCode,
    kosher,
    skipInventoryHold: false,
    gimmonixHotelId: row.gimmonixHotelId,
    isOpsOverlay: true,
    badgeText: kosher ? "מחירים מיוחדים · כשר" : "מחירים מיוחדים",
  };
}

export function readCampaignHotelOverlay(storage = defaultStorage()) {
  if (storage) {
    try {
      const raw = storage.getItem(CAMPAIGN_HOTEL_OPS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const hotels = Array.isArray(parsed?.hotels)
          ? parsed.hotels.map(normalizeOverlayHotel).filter(Boolean)
          : [];
        const extraRoomsByHotel =
          parsed?.extraRoomsByHotel && typeof parsed.extraRoomsByHotel === "object"
            ? parsed.extraRoomsByHotel
            : {};
        const hotelPatchesByHotel =
          parsed?.hotelPatchesByHotel && typeof parsed.hotelPatchesByHotel === "object"
            ? Object.fromEntries(
                Object.entries(parsed.hotelPatchesByHotel)
                  .map(([code, patch]) => [String(code), normalizeHotelPatch(patch)])
                  .filter(([, patch]) => patch),
              )
            : {};
        memoryOverlayState = collapseOverlayStateAliases({
          version: 1,
          hotels,
          extraRoomsByHotel,
          hiddenHotelCodes: Array.isArray(parsed?.hiddenHotelCodes)
            ? parsed.hiddenHotelCodes.map(String).filter(Boolean)
            : [],
          hiddenRoomCodesByHotel:
            parsed?.hiddenRoomCodesByHotel && typeof parsed.hiddenRoomCodesByHotel === "object"
              ? parsed.hiddenRoomCodesByHotel
              : {},
          voucherRecipientsByHotel:
            parsed?.voucherRecipientsByHotel && typeof parsed.voucherRecipientsByHotel === "object"
              ? parsed.voucherRecipientsByHotel
              : {},
          hotelPatchesByHotel,
        });
        return cloneOverlayState(memoryOverlayState);
      }
    } catch {
      /* fall through to memory */
    }
  }
  return cloneOverlayState(memoryOverlayState);
}

export function writeCampaignHotelOverlay(state, storage = defaultStorage()) {
  const next = collapseOverlayStateAliases({
    version: 1,
    hotels: (state?.hotels || []).map(normalizeOverlayHotel).filter(Boolean),
    extraRoomsByHotel:
      state?.extraRoomsByHotel && typeof state.extraRoomsByHotel === "object"
        ? state.extraRoomsByHotel
        : {},
    hiddenHotelCodes: [...new Set((state?.hiddenHotelCodes || []).map(String).filter(Boolean))],
    hiddenRoomCodesByHotel:
      state?.hiddenRoomCodesByHotel && typeof state.hiddenRoomCodesByHotel === "object"
        ? state.hiddenRoomCodesByHotel
        : {},
    voucherRecipientsByHotel:
      state?.voucherRecipientsByHotel && typeof state.voucherRecipientsByHotel === "object"
        ? state.voucherRecipientsByHotel
        : {},
    hotelPatchesByHotel:
      state?.hotelPatchesByHotel && typeof state.hotelPatchesByHotel === "object"
        ? Object.fromEntries(
            Object.entries(state.hotelPatchesByHotel)
              .map(([code, patch]) => [String(code), normalizeHotelPatch(patch)])
              .filter(([, patch]) => patch),
          )
        : {},
  });
  memoryOverlayState = cloneOverlayState(next);
  if (storage) {
    try {
      storage.setItem(CAMPAIGN_HOTEL_OPS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private mode */
    }
  }
  emitOverlayChanged();
  return next;
}

export function listOverlayHotels(storage = defaultStorage()) {
  return readCampaignHotelOverlay(storage).hotels;
}

/**
 * Mirror commercial fields from shared catalog (server) into hotelPatches for
 * built-in campaigns (Belvedere / Tulip / Zanzibar) so pricing picks them up.
 */
export function applyBuiltInCommercialPatchesFromServer(hotels, storage = defaultStorage()) {
  const incoming = (Array.isArray(hotels) ? hotels : []).map(normalizeOverlayHotel).filter(Boolean);
  if (!incoming.length) return readCampaignHotelOverlay(storage);
  const state = readCampaignHotelOverlay(storage);
  let changed = false;
  for (const hotel of incoming) {
    if (!isBuiltInCampaignCode(hotel.hotelCode) && !builtInContractForCode(hotel.hotelCode)) {
      continue;
    }
    const patchCode = campaignHotelAliasGroup(hotel.hotelCode) || hotel.hotelCode;
    const prev = state.hotelPatchesByHotel[patchCode] || {};
    const patch = normalizeHotelPatch({
      ...prev,
      hotelCode: patchCode,
      displayName: hotel.displayName,
      displayNameHe: hotel.displayNameHe,
      currency: hotel.currency,
      kosher: hotel.kosher,
      starRating: hotel.starRating,
      agentMarkupPercent: hotel.agentMarkupPercent,
      publicEffectiveMarkupTargetPercent: hotel.publicEffectiveMarkupTargetPercent,
      minStayNights: hotel.minStayNights,
      seasons: hotel.seasons,
      blackouts: hotel.blackouts,
      destinationCodes: hotel.destinationCodes,
      destinationNameHints: hotel.destinationNameHints,
      gimmonixHotelId: hotel.gimmonixHotelId,
      voucherRecipients: hotel.voucherRecipients,
    });
    if (!patch) continue;
    state.hotelPatchesByHotel[patchCode] = patch;
    if (patch.voucherRecipients) {
      state.voucherRecipientsByHotel[patchCode] = patch.voucherRecipients;
    }
    changed = true;
  }
  if (!changed) return state;
  return writeCampaignHotelOverlay(state, storage);
}

export function mergeServerHotelsIntoOverlay(hotels, storage = defaultStorage()) {
  const incoming = (Array.isArray(hotels) ? hotels : []).map(normalizeOverlayHotel).filter(Boolean);
  if (!incoming.length) return readCampaignHotelOverlay(storage);
  const state = readCampaignHotelOverlay(storage);
  const backendCanonicalCodes = incoming.map((row) => row.hotelCode);
  // Built-ins: commercial SSOT from server → patches (not overlay hotel rows).
  applyBuiltInCommercialPatchesFromServer(incoming, storage);
  const refreshed = readCampaignHotelOverlay(storage);
  Object.assign(state, refreshed);
  for (const hotel of incoming) {
    if (isBuiltInCampaignCode(hotel.hotelCode)) continue;
    const index = state.hotels.findIndex((row) =>
      campaignHotelCodesAreAliases(row.hotelCode, hotel.hotelCode),
    );
    const incomingRooms = roomsEligibleForCatalogPersist(hotel.rooms, {
      inferredHotel: hotel.inferred,
    });
    if (index >= 0) {
      const prev = state.hotels[index];
      const prevRooms = roomsEligibleForCatalogPersist(prev.rooms, {
        inferredHotel: prev.inferred,
      });
      state.hotels[index] = {
        ...prev,
        ...hotel,
        hotelCode: hotel.hotelCode,
        slug: hotel.slug || hotel.hotelCode,
        rooms: incomingRooms.length ? incomingRooms : prevRooms,
        inferred: incomingRooms.length ? false : Boolean(hotel.inferred && prev.inferred),
        createdAt: prev.createdAt || hotel.createdAt,
        updatedAt: hotel.updatedAt || prev.updatedAt,
      };
    } else {
      state.hotels.push({
        ...hotel,
        rooms: incomingRooms,
      });
    }
  }
  return writeCampaignHotelOverlay(
    collapseOverlayStateAliases(state, { backendCanonicalCodes }),
    storage,
  );
}

export function getOverlayHotel(hotelCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  if (!code) return null;
  return (
    listOverlayHotels(storage).find(
      (row) =>
        row.hotelCode === code || campaignHotelCodesAreAliases(row.hotelCode, code),
    ) || null
  );
}

export function listOverlayExtraRooms(hotelCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  const extras = readCampaignHotelOverlay(storage).extraRoomsByHotel?.[code];
  return Array.isArray(extras) ? extras : [];
}

export function listOverlayRoomsForHotel(hotelCode, storage = defaultStorage()) {
  const overlay = getOverlayHotel(hotelCode, storage);
  if (overlay) return overlay.rooms || [];
  return listOverlayExtraRooms(hotelCode, storage);
}

export function mergeDestinationCampaignHotels(baseHotels, storage = defaultStorage()) {
  const base = Array.isArray(baseHotels) ? baseHotels : [];
  const hidden = new Set(
    (readCampaignHotelOverlay(storage).hiddenHotelCodes || []).map((code) => String(code)),
  );
  const overlayRows = listOverlayHotels(storage)
    .map(buildOpsCampaignRow)
    .filter(Boolean)
    .filter(
      (row) =>
        !base.some(
          (existing) =>
            existing.hotelCode === row.hotelCode ||
            campaignHotelCodesAreAliases(existing.hotelCode, row.hotelCode),
        ),
    );
  const combined = [
    ...base
      .filter((row) => !hidden.has(String(row.hotelCode)))
      .map((row) => applyHotelPatchToCampaignRow(row, storage)),
    ...overlayRows,
  ];
  const seenGroups = new Set();
  const collapsed = [];
  for (const row of combined) {
    const group = campaignHotelAliasGroup(row.hotelCode);
    if (seenGroups.has(group)) continue;
    seenGroups.add(group);
    collapsed.push(row);
  }
  return collapsed;
}

export function mergeContractWithOverlayRooms(contract, hotelCode, storage = defaultStorage()) {
  if (!contract) return null;
  const extras = getOverlayHotel(hotelCode, storage)
    ? []
    : listOverlayExtraRooms(hotelCode, storage);
  const withRooms = extras.length
    ? { ...contract, roomTypes: [...(contract.roomTypes || []), ...extras] }
    : contract;
  return applyHotelPatchToContract(withRooms, hotelCode, storage);
}

export function inventoryRoomsFromContract(contract) {
  return (contract?.roomTypes || []).map((room) => ({
    room_code: room.code,
    name: room.name,
    nameHe: room.nameHe || "",
    contract_allotment: room.allotment,
    board: room.board,
  }));
}

export function mergeInventoryRoomLists(apiRooms, fallbackRooms) {
  const byCode = new Map();
  for (const room of fallbackRooms || []) {
    if (room?.room_code == null) continue;
    byCode.set(String(room.room_code), room);
  }
  for (const room of apiRooms || []) {
    if (room?.room_code == null) continue;
    const key = String(room.room_code);
    const prev = byCode.get(key);
    byCode.set(key, prev ? { ...prev, ...room } : room);
  }
  return [...byCode.values()];
}

export function validateHotelDraft(draft, storage = defaultStorage(), { editingCode } = {}) {
  const errors = [];
  const displayName = String(draft?.displayName || "").trim();
  const hotelCode = String(draft?.hotelCode || "").trim();
  const editing = String(editingCode || "").trim();
  if (!displayName) errors.push("שם באנגלית הוא שדה חובה");
  if (!hotelCode) errors.push("קוד קמפיין הוא שדה חובה");
  else if (!campaignCodeIsValid(hotelCode)) {
    errors.push("קוד קמפיין: אותיות באנגלית קטנות, מספרים ומקפים בלבד");
  } else if (
    hotelCode !== editing &&
    (isBuiltInCampaignCode(hotelCode) || getOverlayHotel(hotelCode, storage))
  ) {
    errors.push("קוד קמפיין כבר קיים");
  }
  if (!String(draft?.city || "").trim()) errors.push("עיר היא שדה חובה");
  const stayFrom = draft?.stayFrom || draft?.seasons?.[0]?.from;
  const stayTo = draft?.stayTo || draft?.seasons?.[0]?.to;
  if (!stayFrom || !stayTo) errors.push("יש לבחור חלון שהייה");
  if (draft?.kosher !== true && draft?.kosher !== false) {
    errors.push("יש לבחור האם המלון כשר");
  }
  return errors;
}

export function validateRoomDraft(draft) {
  const errors = [];
  if (!String(draft?.name || "").trim()) errors.push("שם חדר באנגלית הוא שדה חובה");
  const boards = Array.isArray(draft?.boards) ? draft.boards : [];
  if (!boards.length) errors.push("יש לבחור לפחות בסיס אירוח אחד");
  const pricing = normalizeOpsRoomPricing(draft);
  if (pricing.pricingKind === "supplement") {
    if (!pricing.baseRoomCode && !pricing.baseRoomId) {
      errors.push("יש לבחור חדר בסיס לתוספת");
    }
    const hasAdultSup =
      pricing.supplementAdultPerNight != null && Number.isFinite(pricing.supplementAdultPerNight);
    const hasChildSup =
      pricing.supplementChildPerNight != null && Number.isFinite(pricing.supplementChildPerNight);
    if (!hasAdultSup && !hasChildSup) {
      errors.push("יש להזין תוספת למבוגר או לילד");
    }
  } else {
    const nightly = Number(draft?.nightlyNet ?? draft?.adultNightlyNet);
    const hasNightly = Number.isFinite(nightly) && nightly > 0;
    const hasSeasonRate =
      draft?.seasonalRates && Object.values(draft.seasonalRates).some((v) => Number(v) > 0);
    if (!hasNightly && !hasSeasonRate) {
      errors.push("יש להזין מחיר נטו ללילה");
    }
  }
  if (Number(draft?.allotment) < 0) errors.push("מלאי הסכם אינו יכול להיות שלילי");
  return errors;
}

export function upsertOverlayHotel(input, storage = defaultStorage()) {
  const hotel = normalizeOverlayHotel({
    ...input,
    createdAt: input?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  if (!hotel) {
    throw new Error("לא ניתן לשמור מלון — חסר שם או קוד קמפיין");
  }
  if (isBuiltInCampaignCode(hotel.hotelCode)) {
    throw new Error("לא ניתן לדרוס מלון מובנה");
  }
  const state = readCampaignHotelOverlay(storage);
  const index = state.hotels.findIndex((row) => row.hotelCode === hotel.hotelCode);
  if (index >= 0) state.hotels[index] = { ...state.hotels[index], ...hotel };
  else state.hotels.push(hotel);
  writeCampaignHotelOverlay(state, storage);
  return hotel;
}

export function updateOverlayHotel(input, storage = defaultStorage()) {
  const code = String(input?.hotelCode || "").trim();
  if (!code) throw new Error("לא ניתן לשמור מלון — חסר שם או קוד קמפיין");
  const before = campaignHotelLogSnapshot(code, storage);
  const patchCode = campaignHotelAliasGroup(code) || code;
  const existingOverlay = getOverlayHotel(code, storage);
  // Ops-created hotels only. Catalog/contract hotels always go through hotelPatches
  // so resolveCampaignContract(mergeContractWithOverlayRooms) sees agent markup.
  if (existingOverlay && !builtInContractForCode(code) && !isBuiltInCampaignCode(code)) {
    const hotel = normalizeOverlayHotel({
      ...existingOverlay,
      ...input,
      hotelCode: existingOverlay.hotelCode,
      slug: existingOverlay.slug || existingOverlay.hotelCode,
      rooms: existingOverlay.rooms,
      createdAt: existingOverlay.createdAt,
      updatedAt: new Date().toISOString(),
    });
    if (!hotel) throw new Error("לא ניתן לשמור מלון — חסר שם או קוד קמפיין");
    const state = readCampaignHotelOverlay(storage);
    const index = state.hotels.findIndex((row) => row.hotelCode === hotel.hotelCode);
    if (index >= 0) state.hotels[index] = { ...state.hotels[index], ...hotel, rooms: existingOverlay.rooms };
    if (hotel.voucherRecipients) state.voucherRecipientsByHotel[code] = hotel.voucherRecipients;
    writeCampaignHotelOverlay(state, storage);
    return {
      kind: "updated",
      hotel: getOverlayHotel(code, storage),
      before,
      after: campaignHotelLogSnapshot(code, storage),
    };
  }

  // Built-in / catalog campaigns (Belvedere, Tulip, Zanzibar): commercial fields live in
  // hotelPatchesByHotel so resolveCampaignContract → mergeContractWithOverlayRooms picks them up.
  const state = readCampaignHotelOverlay(storage);
  const prev = state.hotelPatchesByHotel[patchCode] || state.hotelPatchesByHotel[code] || {};
  const patch = normalizeHotelPatch({ ...prev, ...input, hotelCode: patchCode });
  if (!patch) throw new Error("לא ניתן לשמור מלון — חסר שם או קוד קמפיין");
  state.hotelPatchesByHotel[patchCode] = patch;
  // Drop stale alias patch keys so lookups stay on the canonical code.
  for (const key of Object.keys(state.hotelPatchesByHotel)) {
    if (key !== patchCode && campaignHotelAliasGroup(key) === patchCode) {
      delete state.hotelPatchesByHotel[key];
    }
  }
  if (patch.voucherRecipients) {
    state.voucherRecipientsByHotel[patchCode] = patch.voucherRecipients;
  }
  writeCampaignHotelOverlay(state, storage);
  return {
    kind: "patched",
    hotelCode: patchCode,
    patch: getOverlayHotelPatch(patchCode, storage),
    before,
    after: campaignHotelLogSnapshot(patchCode, storage),
  };
}

export function addOverlayRooms(hotelCode, drafts, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  const list = Array.isArray(drafts) ? drafts : [drafts];
  const state = readCampaignHotelOverlay(storage);
  const overlayHotel = state.hotels.find((row) => row.hotelCode === code) || null;
  const builtIn = builtInContractForCode(code);
  const seasons = overlayHotel?.seasons || builtIn?.seasons || [];
  const kosher = overlayHotel?.kosher ?? Boolean(builtIn?.kosher);
  const existing = [
    ...(builtIn?.roomTypes || []),
    ...(overlayHotel?.rooms || []),
    ...(state.extraRoomsByHotel[code] || []),
  ];
  let nextCode = nextNumericRoomCode(existing);
  const created = [];
  for (const draft of list) {
    const rooms = expandRoomDraftToContractRooms(draft, nextCode, { kosher, seasons });
    nextCode += rooms.length;
    created.push(...rooms);
  }
  if (overlayHotel) {
    overlayHotel.rooms = [...(overlayHotel.rooms || []), ...created];
    overlayHotel.updatedAt = new Date().toISOString();
  } else {
    state.extraRoomsByHotel[code] = [...(state.extraRoomsByHotel[code] || []), ...created];
  }
  writeCampaignHotelOverlay(state, storage);
  return created;
}

export function isOverlayCreatedHotel(hotelCode, storage = defaultStorage()) {
  return Boolean(getOverlayHotel(hotelCode, storage));
}

export function deleteOverlayHotel(hotelCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  if (!code) throw new Error("חסר קוד קמפיין");
  const state = readCampaignHotelOverlay(storage);
  const index = state.hotels.findIndex((row) => row.hotelCode === code);
  if (index >= 0) {
    const removed = state.hotels[index];
    state.hotels.splice(index, 1);
    delete state.extraRoomsByHotel[code];
    delete state.hiddenRoomCodesByHotel[code];
    delete state.voucherRecipientsByHotel[code];
    delete state.hotelPatchesByHotel[code];
    writeCampaignHotelOverlay(state, storage);
    return { kind: "deleted", hotel: removed };
  }
  if (isBuiltInCampaignCode(code)) {
    if (!state.hiddenHotelCodes.includes(code)) state.hiddenHotelCodes.push(code);
    writeCampaignHotelOverlay(state, storage);
    return { kind: "hidden", hotelCode: code };
  }
  throw new Error("המלון לא נמצא בשכבת התפעול");
}

function addHiddenRoomCode(state, hotelCode, roomKey) {
  const hidden = (state.hiddenRoomCodesByHotel[hotelCode] || []).map(String);
  if (!hidden.includes(roomKey)) {
    state.hiddenRoomCodesByHotel[hotelCode] = [...hidden, roomKey];
  }
}

export function listHiddenRoomCodes(hotelCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  const hidden = readCampaignHotelOverlay(storage).hiddenRoomCodesByHotel?.[code] || [];
  return [...new Set(hidden.map((roomCode) => String(roomCode || "").trim()).filter(Boolean))];
}

export function deleteOverlayRoom(hotelCode, roomCode, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  const roomKey = String(roomCode ?? "").trim();
  if (!code || !roomKey) throw new Error("חסר קוד מלון או חדר");
  const state = readCampaignHotelOverlay(storage);
  const hotel = state.hotels.find((row) => row.hotelCode === code);
  const match = (row) => String(row?.code ?? row?.room_code) === roomKey;
  if (hotel) {
    const found = (hotel.rooms || []).find(match);
    if (found) {
      hotel.rooms = (hotel.rooms || []).filter((row) => !match(row));
      hotel.updatedAt = new Date().toISOString();
      addHiddenRoomCode(state, code, roomKey);
      writeCampaignHotelOverlay(state, storage);
      return { kind: "deleted", room: found };
    }
  }
  const extras = state.extraRoomsByHotel[code] || [];
  const extra = extras.find(match);
  if (extra) {
    state.extraRoomsByHotel[code] = extras.filter((row) => !match(row));
    addHiddenRoomCode(state, code, roomKey);
    writeCampaignHotelOverlay(state, storage);
    return { kind: "deleted", room: extra };
  }
  addHiddenRoomCode(state, code, roomKey);
  writeCampaignHotelOverlay(state, storage);
  return { kind: "hidden", roomCode: roomKey };
}

export function filterHiddenInventoryRooms(rooms, hotelCode, storage = defaultStorage()) {
  const hidden = new Set(
    (readCampaignHotelOverlay(storage).hiddenRoomCodesByHotel?.[String(hotelCode || "")] || []).map(
      String,
    ),
  );
  if (!hidden.size) return Array.isArray(rooms) ? rooms : [];
  return (rooms || []).filter((row) => !hidden.has(String(row?.room_code ?? row?.code)));
}

export function getOverlayVoucherRecipients(hotelCode, storage = defaultStorage()) {
  const overlay = getOverlayHotel(hotelCode, storage);
  if (overlay?.voucherRecipients?.length) return overlay.voucherRecipients;
  const sidecar = readCampaignHotelOverlay(storage).voucherRecipientsByHotel?.[String(hotelCode || "")];
  return normalizeVoucherRecipients(sidecar);
}

export function setOverlayVoucherRecipients(hotelCode, recipients, storage = defaultStorage()) {
  const code = String(hotelCode || "").trim();
  const list = normalizeVoucherRecipients(recipients);
  const state = readCampaignHotelOverlay(storage);
  const hotel = state.hotels.find((row) => row.hotelCode === code);
  if (hotel) hotel.voucherRecipients = list;
  else state.voucherRecipientsByHotel[code] = list;
  writeCampaignHotelOverlay(state, storage);
  return list;
}
